import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, useToggle } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'

import { observer } from 'mobx-react'

import InputText from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { useDeprecatedTimeTrackingFeatureAvailability } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import attendancePolicies from 'modules/attendance/store/collections/policies'
import attendanceSettings from 'modules/attendance/store/collections/settings'
import { APPS } from 'modules/billing/lib/types'

export interface RenderProps {
  onClick: () => void
  label?: string
}

export const Empty = ({ children }: React.ConsumerProps<RenderProps | null>) => (
  <>{children(null)}</>
)

const AddPolicyButton = ({ children }: React.ConsumerProps<RenderProps>) => {
  const navigate = useNavigate()
  const [addModalOpen, toggleAddModal] = useToggle(false)
  const [warningModalOpen, toggleWarningModal] = useToggle(false)
  const { navigation } = useNavigation()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const timeTrackingMultiPoliciesEnabled = useDeprecatedTimeTrackingFeatureAvailability(
    companyId,
    'time_tracking_multi_policies'
  )

  const policyForm: FactorialForm = React.useRef(
    new FactorialForm(
      {
        name: i18n.t('app_store_v2.apps.time_tracking.new_policy_name'),
        description: null,
      },
      {
        name: 'string',
        description: 'string',
      }
    )
  ).current

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const policyResult = await asyncAction(() =>
      attendancePolicies.create({
        name: i18n.t('app_store_v2.apps.time_tracking.new_policy_name'),
        enabled: true,
        default: false,
        ...policyForm.data(),
      })
    )

    if (isSuccessful(policyResult)) {
      await asyncAction(() =>
        attendanceSettings.fetch({ data: { employees_policy_ids: [+policyResult.value.id] } })
      )

      const attendanceSetting = attendanceSettings.currentByPolicy(policyResult.value.id)
      const routeParams = {
        id: APPS.time_tracking,
        settingId: attendanceSetting?.attributes.id.toString(),
        tabId: 'edit',
      }
      navigate(navigation.appStore.id.settings.settingId.tabId.fullPath(routeParams))

      closePortal()
    }
  }

  return (
    <>
      {children({
        label: i18n.t('time_tracking.settings.add_policy'),
        onClick: timeTrackingMultiPoliciesEnabled ? toggleAddModal(true) : toggleWarningModal(true),
      })}
      {warningModalOpen && (
        <Modal
          illustration="fireworks"
          title={i18n.t('app_store_v2.apps.time_tracking.warning_modal.title')}
          description={i18n.t('app_store_v2.apps.time_tracking.warning_modal.description')}
          onClose={toggleWarningModal(false)}
          footer={
            <>
              <Button type="secondary" onClick={toggleWarningModal(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button.Router
                to={navigation.settings.plans.fullPath()}
                onClick={toggleWarningModal(false)}
              >
                {i18n.t('app_store_v2.apps.time_tracking.warning_modal.button')}
              </Button.Router>
            </>
          }
        ></Modal>
      )}
      {addModalOpen && (
        <Modal
          illustration="calendar-clock"
          title={i18n.t('app_store_v2.apps.time_tracking.create_modal.title')}
          description={i18n.t('app_store_v2.apps.time_tracking.create_modal.description')}
          onClose={toggleAddModal(false)}
          onSubmit={onSubmit}
          footer={
            <Button type="primary" disabled={policyForm.fields.name?.value.length === 0} submit>
              {i18n.t('app_store_v2.apps.time_tracking.create_modal.button')}
            </Button>
          }
        >
          <InputText
            label={i18n.t('time_tracking.settings.form.policy_name')}
            field={policyForm.get('name')}
          />
          <TextArea
            label={i18n.t('time_tracking.settings.form.policy_description')}
            field={policyForm.get('description')}
          />
        </Modal>
      )}
    </>
  )
}

export default observer(AddPolicyButton)
